<template>
  <teleport to="body">
    <WdsModal md :show="open" :title="$t('Finalise offer')" @cancel="emit('close')">
      <div v-if="loading" class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
        <WdsSpinner indeterminate />
      </div>

      <WdsStack v-else direction-column gap-md>
        <WdsAlert
          v-if="triggerApproval"
          warning
          icon
          :title="
            $t(
              'Hire requires internal approval first. Once approved, the offer will be automatically sent to the worker.',
            )
          "
          :description="$t(`The contract can't be edited while pending approval.`)"
        />
        <WdsStack direction-column gap-sm>
          <WdsText weight="bold">
            <template v-if="hasRecruiterManagingWorkers">
              {{ $t('Message to the staffing agency:') }}
              <WdsTag sm>{{ $t('Optional') }}</WdsTag>
            </template>
            <template v-else>
              {{ $t('Message to the worker:') }}
              <WdsTag sm>{{ $t('Optional') }}</WdsTag>
            </template>
          </WdsText>

          <WdsInputArea
            v-model="message"
            height="150px"
            required
            :placeholder="$t('Optional message about the offer...')"
            :error="validationErrors?.['input.message']"
          />

          <WdsAlert
            info-subtle
            :title="$t('What’s next')"
            :description="
              $t(
                hasRecruiterManagingWorkers
                  ? 'Once you share the offer, the staffing agency will be able to review the details and complete any remaining information needed to finalise the contract.'
                  : 'Once you share the offer, the worker will be able to review the details and complete any remaining information needed to finalise the contract.',
              )
            "
          />
        </WdsStack>
      </WdsStack>

      <template #footer>
        <WdsStack v-if="!loading" justify-space-between gap-sm>
          <WdsButton :disabled="shareLoading" @click="emit('close')">
            {{ $t('Cancel') }}
          </WdsButton>

          <WdsStack gap-sm align-center>
            <WdsSpinner v-if="shareLoading" indeterminate />
            <WdsButton
              primary
              gap
              :disabled="shareLoading"
              class="tw-items-center tw-gap-1"
              @click="handleShare"
            >
              <template v-if="triggerApproval">
                {{ $t('Send for approval') }}
                <WdsIcon name="send-alt" :size="16" />
              </template>
              <template v-else>
                {{ $t('Share') }}
                <WdsIcon name="send-alt" :size="16" />
              </template>
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import {
  WdsButton,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
  WdsInputArea,
  WdsAlert,
  WdsTag,
  WdsIcon,
} from '@worksome/wds-components'

import { useHireRecruiter } from './composables/useHireRecruiter'
import { graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { Notification, NetworkError } from '@/utils/notification'
import useApolloError from '@/composables/useApolloError'
import enums from '@/utils/enum'
import queriesToRefetch from '@/components/Hire/queriesToRefetch'
import { publicApiCustomFields } from '@/app-bootstrapping/SetUpApollo'

const props = withDefaults(
  defineProps<{
    open?: boolean
    hireId: string
  }>(),
  {
    open: false,
  },
)

const { hasRecruiterManagingWorkers } = useHireRecruiter(props.hireId)

const emit = defineEmits(['close'])

const ShareHireFragment = graphql(`
  fragment ShareHireFields on Hire {
    id
    recruiterManagesWorkers
    job {
      id
      name
    }
    triggersApproval(trigger: HIRE_CREATED)
    worker {
      id
      name
      firstName
    }
    recruiter {
      id
      name
    }
  }
`)

const GET_HIRE_FOR_SHARE = graphql(`
  query GetHireForShare($id: ID!) {
    hire(id: $id) {
      ...ShareHireFields
    }
  }
`)

const SHARE_HIRE = graphql(`
  mutation ShareHire($input: ShareHireInput!) {
    shareHire(input: $input) {
      ...ShareHireFields
    }
  }
`)

const { result, loading, refetch } = useQuery(GET_HIRE_FOR_SHARE, () => ({ id: props.hireId }), {
  clientId: 'publicApi',
})

const {
  mutate: shareHire,
  loading: shareLoading,
  error: shareError,
} = useMutation(SHARE_HIRE, {
  clientId: 'publicApi',
  refetchQueries: queriesToRefetch,
})

const { validationErrors } = useApolloError(shareError)

const message = ref<string>('')

const hire = computed(() => useFragment(ShareHireFragment, result.value?.hire))

const triggerApproval = computed(() => hire.value?.triggersApproval)

watchEffect(() => {
  // Only set the initial value when hire is loaded and message is empty
  if (hire.value && message.value === '') {
    message.value = hasRecruiterManagingWorkers.value
      ? $t(
          'Hi, I’m sending over a hire offer for this role. Please take a look and let me know if you have any questions.  Kind regards, :username from :account_name',
          {
            account_name: currentAccount.name,
            username: currentUser.name,
          },
        )
      : $t(
          'Hi:optional_worker_name, I’m sending over a hire offer for this role. Please take a look and let me know if you have any questions.  Kind regards, :username from :account_name',
          {
            account_name: currentAccount.name,
            username: currentUser.name,
            optional_worker_name: hire.value?.worker?.firstName ?? '',
          },
        )
  }
})

const handleShare = async () => {
  try {
    await shareHire({
      input: {
        hire: props.hireId,
        message: message.value.trim(),
      },
    })

    const msgContent = triggerApproval.value
      ? $t('The hire was sent for approval')
      : $t(
          hasRecruiterManagingWorkers.value
            ? 'The hire was shared with the staffing agency'
            : 'The hire was shared with the worker',
        )

    new Notification({
      type: enums.notification.types.NETWORK,
      msg: msgContent,
    }).dispatch()

    await publicApiCustomFields.refetchQueries({
      include: ['HirePageData'],
    })

    emit('close')
  } catch (error: any) {
    const errorMessage =
      validationErrors.value?.['input.hire'] ||
      validationErrors.value?.['input.message'] ||
      $t('The hire could not be shared')

    new NetworkError({
      msg: errorMessage,
      ex: error,
    }).dispatch()
  }
}
</script>
